/**
 * Chart Download Button
 *
 * Drop-in dropdown that opens the dashboard export endpoint with the chosen
 * format. The backend export endpoint is being built by another agent — this
 * button calls it speculatively so the UI is wired the moment the endpoint
 * lands.
 */

import { Button } from '../../../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../../components/ui/dropdown-menu';
import { Download, FileImage, FileText, FileSpreadsheet, FileType } from 'lucide-react';

type ExportFormat = 'png' | 'pdf' | 'xlsx' | 'csv';

interface ChartDownloadButtonProps {
  dashboardId?: number;
  indicatorId?: number;
  /** Override the URL builder if the host wants to call a different endpoint. */
  buildUrl?: (format: ExportFormat) => string;
}

const FORMAT_OPTIONS: { format: ExportFormat; label: string; icon: typeof FileImage }[] = [
  { format: 'png', label: 'PNG image', icon: FileImage },
  { format: 'pdf', label: 'PDF document', icon: FileText },
  { format: 'xlsx', label: 'Excel workbook', icon: FileSpreadsheet },
  { format: 'csv', label: 'CSV file', icon: FileType },
];

function defaultUrl(dashboardId: number | undefined, indicatorId: number | undefined, format: ExportFormat): string {
  const params = new URLSearchParams({ format });
  if (indicatorId !== undefined) params.set('indicator', String(indicatorId));
  const id = dashboardId ?? 0;
  return `/api/v1/reporting/dashboards/${id}/export/?${params.toString()}`;
}

export function ChartDownloadButton({ dashboardId, indicatorId, buildUrl }: ChartDownloadButtonProps) {
  const handleDownload = (format: ExportFormat) => {
    const url = buildUrl ? buildUrl(format) : defaultUrl(dashboardId, indicatorId, format);
    window.open(url, '_blank', 'noopener');
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="h-7 w-7" aria-label="Download chart">
          <Download className="h-3.5 w-3.5" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {FORMAT_OPTIONS.map(({ format, label, icon: Icon }) => (
          <DropdownMenuItem key={format} onClick={() => handleDownload(format)}>
            <Icon className="h-4 w-4 mr-2" />
            {label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
